<template>
	<ja-modal
		v-if="paymentSummary"
		ref="modal"
		class="scrollable-modal"
		:show="show"
	>
		<content class="life-stage-summary">
			<div class="inner-wrapper">
				<h1 class="heading-5">{{ $t('lifeStageSummary') }}</h1>
				<h2 class="heading-1">
					{{
						$t('lifeStageSummaryMsg', {
							lifeStageName: profileData.cmsLifeStage.name
						})
					}}
				</h2>
				<div
					v-if="!profileData.isEntryVirtualSimulationType"
					class="summary-wrapper"
				>
					<div class="summary-section">
						<ja-finance-progress-bar></ja-finance-progress-bar>
						<ja-financial-health-info
							class="financial-health-section"
						></ja-financial-health-info>
					</div>
				</div>
				<ja-accordion class="summary-wrapper" :dark-mode="true" :tabs="tabs">
				</ja-accordion>
			</div>
			<footer>
				<ja-local-sponsor
					v-if="localSponsor"
					:logo="localSponsor"
				></ja-local-sponsor>
				<div class="button-wrapper">
					<ja-button
						v-if="!nextLifeStageName"
						class="large"
						name="finish-life-stage"
						@click.native.once="endLifeStage"
						>{{ $t('finish') }}</ja-button
					>
					<ja-button
						v-else-if="profileData.isEntryVirtualSimulationType"
						class="large"
						name="finish-life-stage"
						@click.native.once="endLifeStage"
						>{{ $t('continue') }}</ja-button
					>
					<ja-button
						v-else
						class="large"
						name="finish-life-stage"
						@click.native.once="endLifeStage"
						>{{
							$t('lifeStageSummaryBtn', {
								lifeStageName: nextLifeStageName
							})
						}}</ja-button
					>
				</div>
			</footer>
		</content>
	</ja-modal>
</template>
<script>
import '../icons/financialStatus';
import Modal from './Modal';
import UserData from '@/mixins/UserData';
import LifeStageData from '@/mixins/LifeStageData';
import ProfileData from '@/mixins/ProfileData';
import ModalEvents from '@/mixins/ModalEvents';
import ChartData from '@/mixins/ChartData';
import FinancialHealthInfo from '../FinancialHealthInfo/FinancialHealthInfo';
import Accordion from '../ui/Accordion';
import FinancialSummary from '../FinancialSummary/FinancialSummary';
import FinanceProgressBar from '../Metrics/FinanceProgressBar';
import Button from '../ui/Button';
import LocalSponsor from '@/components/ui/LocalSponsor';
import LifeStageService from '@/services/LifeStageService';

export default {
	components: {
		jaModal: Modal,
		jaButton: Button,
		jaAccordion: Accordion,
		jaFinancialHealthInfo: FinancialHealthInfo,
		jaFinanceProgressBar: FinanceProgressBar,
		jaLocalSponsor: LocalSponsor
	},
	mixins: [UserData, LifeStageData, ProfileData, ChartData, ModalEvents],
	props: {
		show: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			tabs: [
				{
					title: this.$t('oneMonthSpending'),
					component: FinancialSummary,
					props: {}
				},

				{
					title: this.$t('projectedSpending'),
					component: FinancialSummary,
					props: {}
				}
			],
			nextLifeStageName: null,
			paymentSummary: null,
			monthsInYear: 12,
			creditCardExpenses: null,
			creditCardPayments: {
				debt: 0,
				payedAmount: 0
			}
		};
	},
	computed: {
		totalCreditCardExpenses() {
			let result = 0;
			if (this.creditCardExpenses) {
				result = this.creditCardExpenses;
			}
			if (this.lifeStageIsYA) {
				result += this.creditCardStartingBalance;
			}
			return result;
		},
		localSponsor() {
			return this.$store.getters.localSponsor('LifeStageSummary');
		},

		debts() {
			return this.$store.getters.debts;
		}
	},
	watch: {
		paymentSummary(value) {
			this.tabs[0].props.chartsData = this.getMonthlySpendingSummary(value);

			this.setSectionSubtitles();

			this.tabs[1].props.chartsData = this.getProjectedSpendingSummary(value);
		}
	},
	created() {
		if (this.lifeStageData.nextLifeStageID) {
			LifeStageService.getLifeStageName(
				this.lifeStageData.nextLifeStageID
			).then(lifeStageName => {
				this.nextLifeStageName = lifeStageName;
			});
		}
		// TODO: Refactor this to use async/await instead of then
		LifeStageService.getLifeStagePaymentSummary(this.userData.studentId).then(
			paymentSummary => {
				LifeStageService.getStudentCreditCardPayments(
					this.userData.studentId,
					this.userData.studentLifeStageId
				).then(data => {
					this.creditCardPayments = data;
					// Set it here to verify that the content will appear correctly
					// this property is used for modal's showing as well
					this.paymentSummary = paymentSummary;
				});
			}
		);

		LifeStageService.getCreditCardExpensesForCurrentLifeStage(
			this.userData.studentId,
			this.userData.studentLifeStageId
		).then(totalExpenses => {
			this.creditCardExpenses = totalExpenses;
		});
	},
	methods: {
		setSectionSubtitles() {
			const years =
				this.profileData.cmsLifeStage.timeSpanMonths / this.monthsInYear;
			if (this.lifeStageIsTeenager) {
				this.tabs[0].props.subtitle = this.$t(
					'oneMonthSpendingWithoutCreditCard'
				);

				this.tabs[1].props.subtitle = this.$t(
					'projectedSpendingWithoutStudentLoan',
					{
						years: years
					}
				);
			} else {
				this.tabs[0].props.subtitle = this.$t('oneMonthSpendingSubtitle');

				if (this.educationIsFree) {
					this.tabs[1].props.subtitle = this.$t(
						'projectedSpendingWithoutStudentLoan',
						{
							years: years
						}
					);
				} else {
					this.tabs[1].props.subtitle = this.$t('projectedSpendingSubtitle', {
						years: years
					});
				}
			}
		},
		getMonthlySpendingSummary() {
			let result = [this.getNMIChart(this.paymentSummary)];
			if (!this.lifeStageIsTeenager) {
				const { creditLimit } = this.debts.creditCard;
				const availableCredit = creditLimit - this.totalCreditCardExpenses;

				result.push({
					title: this.$t('creditCardPurchases'),
					fullAmount: this.totalCreditCardExpenses,
					smallText: true,
					ariaLabel: this.$t('creditAccountChart', {
						limit: this.totalCreditCardExpenses
					}),
					useEmptyData: true,
					dataset: [
						{
							title: this.$t('amountPaid'),
							value: this.creditCardPayments.payedAmount,
							ariaLabel: this.$t('amountSpentChart', {
								amountSpent: this.creditCardPayments.payedAmount
							})
						},
						{
							title: this.$t('amountLeftToPay'),
							value: this.creditCardPayments.debt,
							ariaLabel: this.$t('amountLeftChart', {
								amountLeft: this.creditCardPayments.debt
							})
						},
						{
							title: this.$t('availableCredit'),
							value: availableCredit,
							labelOnly: true,
							useTick: false
						}
					]
				});
			}
			return result;
		},
		getProjectedSpendingSummary(data) {
			let result = [
				this.getSavingsChart(data.studentGoals, {
					amountProp: 'totalSavingsIncludingCurrentLifeStage',
					isPerMonth: false
				})
			];
			if (!(this.lifeStageIsTeenager || this.educationIsFree)) {
				const studentLoanDebt = Math.max(
					this.paymentSummary.studentLoanDebt,
					0
				);
				const paidDebt = this.paymentSummary.totalStudentLoan - studentLoanDebt;

				result.push({
					ariaLabel: this.$t('postLoanProjectedChart', {
						amount: this.paymentSummary.totalStudentLoan
					}),
					title: this.$t('postsecondaryLoan'),
					fullAmount: this.paymentSummary.totalStudentLoan,
					smallText: true,
					dataset: [
						{
							title: this.$t('paid'),
							value: paidDebt,
							ariaLabel: this.$t('paidDebtChart', { amount: paidDebt })
						},
						{
							title: this.$t('remainingBalance'),
							value: studentLoanDebt,
							ariaLabel: this.$t('dueDebtChart', { amount: studentLoanDebt })
						}
					]
				});
			}
			return result;
		},
		getTotalSavingsAmount(goals) {
			return goals.reduce((sum, item) => {
				if (item.totalSavingsIncludingCurrentLifeStage >= 0) {
					return sum + parseInt(item.totalSavingsIncludingCurrentLifeStage);
				}
			}, 0);
		},
		async endLifeStage() {
			this.$store.commit('setLoading', true);

			await LifeStageService.endLifeStage(
				this.lifeStageData.id,
				this.lifeStageData.nextLifeStageID
			);

			await LifeStageService.updateBudgetModalVisibility(
				this.userData.studentId,
				false
			);

			this.$store.dispatch('getLifeStageData');
			this.$router.push({ name: 'knowledge' });
		}
	}
};
</script>
<style lang="scss" scoped>
.life-stage-summary {
	width: 100%;
	height: 100%;
	.inner-wrapper {
		width: 100%;
		padding: 64px 32px 32px 32px;
		.heading-1 {
			margin: 8px 0 24px 0;
		}

		.summary-section {
			background-color: $page-bgcolor;
			padding: 32px 48px 32px 48px;
			border-radius: 8px;

			.financial-health-section {
				margin-top: 24px;
			}
		}

		.financial-summary-wrapper {
			display: block;
			list-style-type: none;
			padding: 0;
			margin: 0 0 32px 0;
			box-shadow: none;
			background-color: $page-bgcolor;
			border: 0;
			padding: 0;

			.title-wrapper {
				padding: 24px 32px;
				h3 {
					margin: 0;
					text-align: left;
				}
			}

			.component-wrapper {
				padding: 32px 48px 64px 48px;
				border-top: 1px solid $container-border;
			}
		}
	}

	footer {
		width: 100%;
		height: 90px;
		padding: 24px 32px;
		display: flex;
		align-items: center;

		.button-wrapper {
			flex-grow: 1;
			text-align: right;
		}
		img {
			max-height: 82px;
		}
	}

	.modal-wrapper {
		width: 100%;
		padding-bottom: 32px;
		border-bottom: 1px solid $container-border;

		&.chart {
			padding-top: 32px;
		}

		.heading-1 {
			margin: 16px;
		}
	}
}
</style>
